<template>
  <div
    :itemscope="useSchema"
    :itemtype="useSchema ? 'http://schema.org/ImageObject' : undefined"
    :class="[
      'p-card',
      'product-card',
      'js-test-tile-de-model',
      'product-card__offer',
      'product-card__tile',
      { skeleton: !offer.id },
      { viewed },
    ]"
    :data-lazy="lazyImg && offer.id ? offer.id : null"
    :data-id="null"
    :data-rid="offer && offer.id"
  >
    <div
      ref="card"
      class="product-card__analytics"
      :data-slot="position"
      :data-url="objToBase64({ url: offer.view_url })"
      :data-block="blockType"
      :data-type="'offer'"
      :data-offer_id="offer.id ? offer.id : ''"
      :data-rid="offer && offer.id"
    />
    <DebugInfo v-if="debugPanel" type="demodel" :data="offer" />
    <template v-if="offer.id">
      <div
        v-if="isMinPrice"
        class="product-card__marker product-card__offer-sticker"
      >
        <UiStickerMonitoring type="minimal" />
      </div>
      <div
        v-if="isPercent"
        class="product-card__marker product-card__offer-sticker"
        :class="{ 'product-card__marker-double': isMinPrice }"
      >
        <UiStickerMonitoring type="percent" :text="offer.discount.percent" />
      </div>

      <infoWindow v-if="offer" @show-view-offer="openQuickView" />

      <div class="product-card__slider pointer">
        <div
          v-if="offer.review_count || offer.rating"
          :class="[
            'event-none',
            'product-card__scores',
            offer.rating ? '' : 'no-rating',
            { 'l-gap-padding': !offer.review_count },
          ]"
        >
          <ratingMerchant
            :class="{ 'l-gap-padding': !offer.review_count }"
            :rating="offer.rating"
            :model="model.model_info"
            :model-path="modelPath"
            :is-tile="true"
          />
        </div>
        <ui-product-card-slider
          :model="offer"
          :lazy-img="lazyImg"
          :index="index"
          :offers="offers"
          type="offer"
          block-type="tile"
          :position="position"
          :model-path="modelPath"
          :disabled="isPlaceOrder"
        />
      </div>
      <div class="product-card__info-description">
        <div class="offer-price">
          <Currency
            v-if="offer"
            type="new"
            :value="ProductPrice"
            prefix=""
            class="product-card__price price-s"
          />
          <Currency
            v-if="isCurrency"
            class="product-card__price-old body-s-strikethrough"
            :value="offer.discount.price_before"
          />
        </div>
        <cpc-link
          :to="offer.click_url"
          :offer="offer"
          :position="position"
          :block-type="blockType"
          :disabled="isPlaceOrder"
        >
          <div class="product-card__title body-l-medium">{{ modelName }}</div>
        </cpc-link>
        <modelViews :count-views="countViews" />

        <div class="product-card__buttons">
          <cpc-link
            v-if="!isPlaceOrder"
            :to="offer.click_url"
            :offer="offer"
            :position="position"
            :block-type="blockType"
          >
            <ui-ui-button
              mode="div"
              size="medium"
              :loading="!offer.id"
              type="outline"
              responsive-ico
              icon-after="link-external"
              :resize="true"
            >
              В магазин
            </ui-ui-button>
          </cpc-link>
          <ui-ui-button
            v-else
            type="outline"
            :resize="true"
            size="medium"
            :loading="!offer.id"
            @click="openOrderModal"
          >
            Сделать заказ
          </ui-ui-button>
        </div>
        <div class="product-card__basement">
          <shop-rating
            v-if="offer.shop_info?.name"
            :shop="offer"
            class="price-card__basement offer"
          />
          <div v-else class="empty-shop-rating" />
        </div>
      </div>
      <div v-if="isBot" :itemprop="useSchema ? 'description' : undefined">
        <div
          v-if="offer.description || offer.specifications"
          class="p-card__characteristic-text"
        >
          <span
            v-for="(spec, index) in offer.specifications"
            :key="index"
            class="specifications"
          >
            {{ index > 0 ? spec.title.toLowerCase() : spec.title }}:
            {{ spec.value }}
            <span
              v-show="index < offer.specifications?.length - 1"
              class="comma"
              >,
            </span>
          </span>
        </div>
      </div>
    </template>

    <template v-else>
      <div class="p-card__slider">
        <div class="p-card__skeleton_img" />
      </div>
      <div class="p-card__info-description">
        <div class="p-card__title" />
      </div>
      <div class="p-card__price" />
      <div class="p-card__info-price">
        <p class="p-card__price-self-pickup" />
      </div>
    </template>
    <QuickViewOffer
      v-if="showQuickViewOffer"
      :model="offer"
      @order="openOrderModal"
      @close="showQuickViewOffer = false"
    />
  </div>
</template>
<script setup lang="ts">
import infoWindow from '../productCardTileOffer/infoWindowTileOffer.vue';
import shopRating from '~/components/ui/storeRating/storeRatingNotModel.vue';
import ratingMerchant from '~/components/ui/rating-merchant/rating-merchant.vue';
import cpcLink from '~/components/ui/CpcLink.vue';
import DebugInfo from '~/components/debug-info/DebugInfo.vue';
import Currency from '~/components/ui/currency/Currency.vue';
import { useAnalyticsStore } from '~/stores/analytics';
import { useGlobalStore } from '~/stores/global';
import { useInteractiveStore } from '~/stores/interactive';
import modelViews from '~/components/page-model/model-views/ModelViews.vue';
import { useModalsStore } from '~/stores/modals';
import { objToBase64 } from '~/utils/base64';

const QuickViewOffer = defineAsyncComponent(
  () => import('~/components/modals/quick-view-offer/quick-view-offer.vue')
);
const props = defineProps({
  model: {
    type: Object as () => any,
  },
  offers: Array,
  lazyImg: {
    type: Boolean,
    default: false,
  },
  nuxtLazy: {
    type: Boolean,
    default: false,
  },
  isBot: {
    type: Boolean,
    default: false,
  },
  viewed: {
    type: Boolean,
    default: false,
  },
  position: {
    type: Number,
    default: 1,
  },
  useSchema: Boolean,
  blockType: {
    type: String,
    default: 'listing',
  },
  addFilterQuery: {
    type: String,
    default: '',
  },
  showMoreButton: {
    type: Boolean,
    default: false,
  },
  index: {
    type: Number,
    default: 0,
  },
});
const card = ref<HTMLElement | undefined>();
const showButtonMoreOnDesktop = ref(false);
const showQuickViewOffer = ref(false);
const { $ymAnyEvent } = useNuxtApp();
const analyticsStore = useAnalyticsStore();
const globalStore = useGlobalStore();
const interactiveStore = useInteractiveStore();
const modalsStore = useModalsStore();
const offer = computed(() => {
  if (props.model.model_info) {
    return {
      ...props.model,
      additional_images: props.model.model_info.additional_images,
      image: props.model.model_info.image,
      name: props.model.model_info.title,
      description: props.model.model_info.description,
      specifications: props.model.model_info.specifications,
      review_count: props.model.model_info.review_count,
      rating: props.model.model_info.rating,
      visit_count: props.model.model_info.visit_count,
    };
  }
  return props.model;
});
const debugPanel = computed(() => globalStore.debugPanel);
const modelName = computed(() => offer.value?.name || '');
const isPlaceOrder = computed(() => offer.value?.cpo);
const isMinPrice = computed(() => props.model?.has_min_price);
const isOrderModal = ref(false);
const countViews = computed(() => {
  return offer.value?.visit_count || 0;
});
const ProductPrice = computed(() => {
  if (offer.value) {
    return offer.value.price || offer.value?.price_info?.min;
  }
  return '';
});

const isPercent = computed<boolean>(
  () =>
    !!offer.value &&
    'discount' in offer.value &&
    !!offer.value.discount &&
    offer.value.discount.percent > 0
);

const isCurrency = computed<boolean>(
  () =>
    !!offer.value &&
    'discount' in offer.value &&
    !!offer.value.discount?.price_before
);

const modelPath = computed(() => {
  const model = offer.value.model_info;
  if (!model) return '';
  const slug = model?.slug || '';
  if (!props.addFilterQuery) return `/${slug}/`;
  return `/${slug}/?${props.addFilterQuery}`;
});

const openOrderModal = () => {
  showQuickViewOffer.value = false;
  modalsStore.setData({
    name: 'order',
    data: {
      offer: props.model,
      position: props.position,
      blockType: props.blockType,
    },
  });
};

const openQuickView = () => {
  $ymAnyEvent('reachGoal', 'click_na_opisanie_ot_magazina');
  showQuickViewOffer.value = true;
};

onMounted(() => {
  if (card.value)
    nextTick(() => {
      if (card.value) {
        analyticsStore.observeView(card.value);
      }
      if (!props.isBot) {
        showButtonMoreOnDesktop.value = true;
      }
    });
});

onBeforeUnmount(() => {
  if (card.value) analyticsStore.unobserveView(card.value);
});
</script>
<style
  scoped
  lang="scss"
  src="../productCardTileOffer/productCardTileOfferScss.scss"
/>

<style scoped lang="scss">
.product-card__basement {
  min-height: 16px;
  text-align: center;
}

.empty-shop-rating {
  height: 16px;
}

.event-none {
  pointer-events: none;
}
</style>
